import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';

import { ErrorSnackbarContext } from '../../features/error-snackbar/error-snackbar-context';
import { StudentBookingErrorResponse } from '../../api/types';
import { ErrorSnackbarActionType } from '../../features/error-snackbar/types';
import { ErrorCode } from '../../shared/enum/ErrorCode';
import { routes } from '../../constants/routes';
import { ValidationErrors } from '../../shared/enum/ValidationErrors';
import { getRedirectState } from '../../utils/local-storage-versioning';
import { buildSearchFromRedirectState } from '../../utils/build-redirect-search-params';

export const useHandleQueryError = () => {
    const navigate = useNavigate();
    const dispatchSnackbarError = useContext(ErrorSnackbarContext);
    const queryClient = useQueryClient();

    return (
        error: Error | AxiosError<StudentBookingErrorResponse>,
        academicYear?: string,
        bookingSummary?: boolean,
        roomsUrl?: string | null
    ) => {
        if (!(error instanceof AxiosError)) return;
        if (!error.response?.data) return;

        const { data, code, message } = error.response.data;

        // Some endpoints (e.g. signing agreements for an STL room) return this as the top level
        // message rather than a room validation error
        if (message === ValidationErrors.ROOM_NOT_AVAILABLE) {
            navigate(routes.roomNotAvailable.index, { state: { roomsUrl } });
            return;
        }

        const redirectState = getRedirectState();
        const search = buildSearchFromRedirectState(redirectState);

        switch (code) {
            case ErrorCode.ERROR_BASKET_OUTDATED:
                dispatchSnackbarError({
                    type: ErrorSnackbarActionType.ERROR_BASKET_OUTDATED,
                    payload: null,
                });
                return;
            case ErrorCode.PROMOTION_EXPIRED:
                queryClient.removeQueries({
                    queryKey: ['createPaymentPlan'],
                });
                dispatchSnackbarError({
                    type: ErrorSnackbarActionType.PROMOTION_EXPIRED,
                    payload: null,
                });
                if (!bookingSummary) {
                    navigate({
                        pathname: routes.summary.index,
                        search: search ? `?${search}` : '',
                    });
                    return;
                }
                return;
            case ErrorCode.ALREADY_HAS_BOOKING:
                dispatchSnackbarError({
                    type: ErrorSnackbarActionType.ALREADY_HAS_BOOKING,
                    payload: academicYear ? { academicYear } : null,
                });
                return;
            default:
        }

        if (data?.room[0]) {
            switch (data.room[0]) {
                case ValidationErrors.ROOM_NOT_AVAILABLE:
                    navigate(routes.roomNotAvailable.index, { state: { roomsUrl } });
                    return;
                case ValidationErrors.BASE_PRICE_MISMATCH:
                    dispatchSnackbarError({
                        type: ErrorSnackbarActionType.BASE_PRICE_MISMATCH,
                        payload: null,
                    });
                    navigate({
                        pathname: routes.summary.index,
                        search: search ? `?${search}` : '',
                    });
                    break;
                default:
            }
        }
    };
};
